import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, withCtx as _withCtx, isRef as _isRef, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withKeys as _withKeys, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue";
const _withScopeId = n => (_pushScopeId("data-v-5f51d1ad"), n = n(), _popScopeId(), n);
const _hoisted_1 = ["crossorigin", "autoplay", "src"];
const _hoisted_2 = ["src", "label", "srcLang"];
import { useDebounceFn, useMediaControls } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppVideoPlayerContentButtonLayout from '@/layouts/AppVideoPlayerContentButtonLayout.vue';
import useResponsiveness from '@/composables/global/use-responsiveness';
import { getClosedCaptionsPref, setClosedCaptionsPref } from '@/utils/user-preferences';
export default /*#__PURE__*/_defineComponent({
  __name: 'AppVideoPlayerContent',
  props: {
    src: {},
    startTime: {},
    isPlaying: {
      type: Boolean
    },
    backgroundColor: {},
    isPremierMode: {
      type: Boolean
    },
    hasAdditionalContent: {
      type: Boolean
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    disabledScrubber: {
      type: Boolean
    },
    closedCaptionSrc: {},
    disabledCrossOrigin: {
      type: Boolean
    },
    isMobileView: {
      type: Boolean
    }
  },
  emits: ["togglePlaying", "toggleFullscreen", "ended"],
  setup(__props, {
    emit: __emit
  }) {
    _useCssVars(_ctx => ({
      "636888a2": _ctx.backgroundColor
    }));
    const emit = __emit;
    const props = __props;
    const {
      locale,
      t
    } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          captionLabel: 'English',
          offLabel: 'Off'
        },
        es: {
          captionLabel: 'Español',
          offLabel: 'Apagado'
        }
      }
    });
    const {
      smDown
    } = useResponsiveness();
    const areControlsShowing = ref(true);
    const isLoading = ref(true);
    const isPlaybackIconShowing = ref(false);
    const wasPlayingBeforeScrub = ref(false);
    const videoRef = ref();
    const playbackIconRef = ref();
    const supposedCurrentTime = ref(0);
    const maxCurrentTime = ref(0);
    const isManuallySeeking = ref(false);
    const volumeButtonIcon = computed(() => volume.value === 0 ? 'volume-off' : volume.value < 0.75 ? 'volume-medium' : 'volume-high');
    const {
      currentTime,
      duration,
      playing,
      rate,
      selectedTrack,
      volume,
      seeking,
      tracks,
      enableTrack,
      disableTrack
    } = useMediaControls(videoRef);
    const debouncedHidePlaybackIcon = useDebounceFn(() => isPlaybackIconShowing.value = false, 500);
    function handleDataLoaded() {
      // Video data has loaded, hide spinner
      isLoading.value = false;
    }
    function handleVideoEnded() {
      // On ended, set playing false
      emit('togglePlaying', false);
      //Emit the video got ended
      emit('ended');
      // Prevent ended video from going to black screen (native mobile)
      currentTime.value = currentTime.value - 0.01;
    }
    onMounted(async () => {
      if (videoRef.value === undefined) return;
      // Synchronize playback state if changed externally by browser
      videoRef.value.addEventListener('play', () => emit('togglePlaying', true));
      videoRef.value.addEventListener('pause', () => emit('togglePlaying', false));
      // Handle video loaded/ended
      videoRef.value.addEventListener('loadedmetadata', handleDataLoaded);
      videoRef.value.addEventListener('ended', handleVideoEnded);
      const closedCaptionsEnabled = await getClosedCaptionsPref();
      if (closedCaptionsEnabled === 'showing' && tracks.value.length > 0) {
        enableTrack(0);
      }
    });
    function formatTime(seconds) {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor(seconds % 3600 / 60);
      const s = Math.round(seconds % 60);
      return [h, m > 9 ? m : h ? '0' + m : m || '0', s > 9 ? s : '0' + s].filter(Boolean).join(':');
    }
    function showPlaybackIcon() {
      // Momentarily overlay video with play/pause icon
      if (playbackIconRef.value === undefined) return;
      playbackIconRef.value.style.animation = 'none';
      requestAnimationFrame(() => {
        // Reset animation
        if (playbackIconRef.value === undefined) return;
        playbackIconRef.value.style.animation = '';
        isPlaybackIconShowing.value = true;
        debouncedHidePlaybackIcon();
      });
    }
    function togglePlaying(fromVideoClicked) {
      if (duration.value - currentTime.value <= 0.02) {
        // Video has ended, restart from beginning
        currentTime.value = 0;
      }
      emit('togglePlaying');
      fromVideoClicked && showPlaybackIcon();
    }
    function handleVideoSeeker() {
      if (!props.disabledScrubber) return;
      const delta = currentTime.value - supposedCurrentTime.value;
      if (delta > 0.01 && videoRef.value !== undefined) {
        if (currentTime.value > maxCurrentTime.value) {
          videoRef.value.currentTime = maxCurrentTime.value;
        } else if (currentTime.value >= supposedCurrentTime.value) {
          videoRef.value.currentTime = supposedCurrentTime.value;
        }
      }
    }
    function handleScrubbingEnded() {
      isManuallySeeking.value = false;
      emit('togglePlaying', wasPlayingBeforeScrub.value);
      handleVideoSeeker();
    }
    function handleScrubbingStarted() {
      isManuallySeeking.value = true;
      wasPlayingBeforeScrub.value = playing.value;
      emit('togglePlaying', false);
    }
    function handleVideoClicked() {
      if (props.isFullscreen) {
        areControlsShowing.value = !areControlsShowing.value;
      } else {
        togglePlaying(true);
      }
    }
    function disableClosedCaptions() {
      disableTrack(0);
      setClosedCaptionsPref('disabled');
    }
    function enableClosedCaptions() {
      enableTrack(0);
      setClosedCaptionsPref('showing');
    }
    watch(() => currentTime.value, () => {
      if (props.disabledScrubber && !seeking.value && !isManuallySeeking.value) {
        supposedCurrentTime.value = currentTime.value;
        if (supposedCurrentTime.value > maxCurrentTime.value) {
          maxCurrentTime.value = supposedCurrentTime.value;
        }
      }
    });
    watch(() => props.isPlaying, () => playing.value = props.isPlaying);
    watch(() => props.startTime, () => currentTime.value = props.startTime);
    watch(() => props.src, () => isLoading.value = true);
    return (_ctx, _cache) => {
      const _component_ion_spinner = _resolveComponent("ion-spinner");
      const _component_ion_icon = _resolveComponent("ion-icon");
      const _component_ion_col = _resolveComponent("ion-col");
      const _component_ion_row = _resolveComponent("ion-row");
      const _component_ion_range = _resolveComponent("ion-range");
      const _component_ion_button = _resolveComponent("ion-button");
      const _component_ion_text = _resolveComponent("ion-text");
      const _component_ion_radio = _resolveComponent("ion-radio");
      const _component_ion_item = _resolveComponent("ion-item");
      const _component_ion_list = _resolveComponent("ion-list");
      const _component_ion_content = _resolveComponent("ion-content");
      const _component_ion_popover = _resolveComponent("ion-popover");
      const _component_ion_label = _resolveComponent("ion-label");
      const _component_ion_grid = _resolveComponent("ion-grid");
      return _openBlock(), _createBlock(_component_ion_grid, {
        class: "ion-no-padding",
        tabindex: "0",
        onKeydown: [_cache[5] || (_cache[5] = _withKeys($event => togglePlaying(true), ["space"])), _cache[6] || (_cache[6] = _withKeys($event => _ctx.isFullscreen && emit('toggleFullscreen', _unref(currentTime)), ["esc"]))]
      }, {
        default: _withCtx(() => [_createVNode(_component_ion_row, {
          onClick: handleVideoClicked,
          class: _normalizeClass(['video-row', 'ion-align-items-center', 'ion-justify-content-center', {
            'fullscreen-video-display-row': _ctx.isFullscreen
          }, {
            'premier-video-display-row': _ctx.isPremierMode
          }, {
            'additional-content': _ctx.hasAdditionalContent
          }, {
            'show-controls': areControlsShowing.value
          }])
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createElementVNode("video", {
              ref_key: "videoRef",
              ref: videoRef,
              crossorigin: _ctx.disabledCrossOrigin ? undefined : 'anonymous',
              playsinline: "",
              preload: "auto",
              autoplay: _ctx.autoplay,
              class: _normalizeClass([{
                fullscreen: _ctx.isFullscreen
              }, {
                premier: _ctx.isPremierMode
              }, {
                'additional-content': _ctx.hasAdditionalContent
              }, {
                'show-controls': areControlsShowing.value
              }]),
              src: `${_ctx.src}\#t=0.01`,
              onEnded: _cache[0] || (_cache[0] = $event => emit('ended'))
            }, [_ctx.closedCaptionSrc ? (_openBlock(), _createElementBlock("track", {
              key: 0,
              src: _ctx.closedCaptionSrc,
              kind: "captions",
              label: _unref(t)('captionLabel'),
              srcLang: _unref(locale)
            }, null, 8, _hoisted_2)) : _createCommentVNode("", true)], 42, _hoisted_1), isLoading.value ? (_openBlock(), _createBlock(_component_ion_spinner, {
              key: 0,
              color: "dark",
              class: "abs-center"
            })) : _createCommentVNode("", true), _createElementVNode("div", {
              ref_key: "playbackIconRef",
              ref: playbackIconRef,
              class: _normalizeClass(['abs-center', 'playback-icon', {
                show: isPlaybackIconShowing.value
              }])
            }, [_withDirectives(_createVNode(_component_ion_icon, {
              name: "play-circle"
            }, null, 512), [[_vShow, _ctx.isPlaying]]), _withDirectives(_createVNode(_component_ion_icon, {
              name: "pause-circle"
            }, null, 512), [[_vShow, !_ctx.isPlaying]])], 2)]),
            _: 1
          })]),
          _: 1
        }, 8, ["class"]), _createVNode(_component_ion_row, null, {
          default: _withCtx(() => [_createVNode(_component_ion_col, null, {
            default: _withCtx(() => [_createVNode(_component_ion_range, {
              max: _unref(duration),
              min: 0,
              mode: "md",
              modelValue: _unref(currentTime),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => _isRef(currentTime) ? currentTime.value = $event : null),
              class: "scrubber video-player-contrast",
              onIonKnobMoveStart: handleScrubbingStarted,
              onIonKnobMoveEnd: handleScrubbingEnded
            }, null, 8, ["max", "modelValue"])]),
            _: 1
          })]),
          _: 1
        }), _createVNode(_component_ion_row, {
          class: "ion-align-items-center ion-justify-content-start ion-nowrap controls-row"
        }, {
          default: _withCtx(() => [_createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_button, {
              onClick: togglePlaying,
              shape: "round",
              class: "video-player-contrast"
            }, {
              default: _withCtx(() => [_withDirectives(_createVNode(_component_ion_icon, {
                name: "play"
              }, null, 512), [[_vShow, !_ctx.isPlaying]]), _withDirectives(_createVNode(_component_ion_icon, {
                name: "pause"
              }, null, 512), [[_vShow, _ctx.isPlaying]])]),
              _: 1
            })]),
            _: 1
          }), _createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_text, {
              class: "duration video-player-contrast"
            }, {
              default: _withCtx(() => [_createTextVNode(_toDisplayString(formatTime(_unref(currentTime))) + " / " + _toDisplayString(formatTime(_unref(duration))), 1)]),
              _: 1
            })]),
            _: 1
          }), _createVNode(_component_ion_col, {
            size: "auto",
            class: "break-end"
          }, {
            default: _withCtx(() => [_createVNode(AppVideoPlayerContentButtonLayout, {
              id: `options-popover-button-${_ctx.isFullscreen}`,
              collapse: _ctx.isMobileView || _unref(smDown)
            }, {
              content: _withCtx(() => [_ctx.closedCaptionSrc ? (_openBlock(), _createBlock(_component_ion_button, {
                key: 0,
                id: `captions-popover-button-${_ctx.isFullscreen}`,
                class: "video-player-contrast"
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_icon, {
                  name: "logo-closed-captioning",
                  slot: "icon-only"
                })]),
                _: 1
              }, 8, ["id"])) : _createCommentVNode("", true), _ctx.closedCaptionSrc ? (_openBlock(), _createBlock(_component_ion_popover, {
                key: 1,
                trigger: `captions-popover-button-${_ctx.isFullscreen}`,
                "show-backdrop": false
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_content, null, {
                  default: _withCtx(() => [_createVNode(_component_ion_list, null, {
                    default: _withCtx(() => [_createVNode(_component_ion_item, {
                      onClick: disableClosedCaptions,
                      lines: "full",
                      button: ""
                    }, {
                      default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)('offLabel')) + " ", 1), _createVNode(_component_ion_radio, {
                        checked: _unref(selectedTrack) === -1,
                        slot: "start"
                      }, null, 8, ["checked"])]),
                      _: 1
                    }), _createVNode(_component_ion_item, {
                      onClick: enableClosedCaptions,
                      lines: "none",
                      button: ""
                    }, {
                      default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(t)('captionLabel')) + " ", 1), _createVNode(_component_ion_radio, {
                        checked: _unref(selectedTrack) === 0,
                        slot: "start"
                      }, null, 8, ["checked"])]),
                      _: 1
                    })]),
                    _: 1
                  })]),
                  _: 1
                })]),
                _: 1
              }, 8, ["trigger"])) : _createCommentVNode("", true), _createVNode(_component_ion_button, {
                id: `rate-popover-button-${_ctx.isFullscreen}`,
                class: "video-player-contrast"
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_icon, {
                  name: "speedometer",
                  slot: "icon-only"
                })]),
                _: 1
              }, 8, ["id"]), _createVNode(_component_ion_popover, {
                trigger: `rate-popover-button-${_ctx.isFullscreen}`,
                "show-backdrop": false
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_content, null, {
                  default: _withCtx(() => [_createVNode(_component_ion_range, {
                    modelValue: _unref(rate),
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => _isRef(rate) ? rate.value = $event : null),
                    max: 2,
                    min: 0.25,
                    step: 0.25,
                    pin: true,
                    snaps: true,
                    class: "video-player-contrast",
                    pinFormatter: value => `${value}x`
                  }, {
                    default: _withCtx(() => [_createVNode(_component_ion_label, {
                      class: "video-player-contrast",
                      slot: "start"
                    }, {
                      default: _withCtx(() => [_createTextVNode(" 0.25x ")]),
                      _: 1
                    }), _createVNode(_component_ion_label, {
                      class: "video-player-contrast",
                      slot: "end"
                    }, {
                      default: _withCtx(() => [_createTextVNode(" 2x ")]),
                      _: 1
                    })]),
                    _: 1
                  }, 8, ["modelValue", "pinFormatter"])]),
                  _: 1
                })]),
                _: 1
              }, 8, ["trigger"]), _createVNode(_component_ion_button, {
                id: `volume-popover-button-${_ctx.isFullscreen}`,
                class: "video-player-contrast"
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_icon, {
                  name: volumeButtonIcon.value,
                  slot: "icon-only"
                }, null, 8, ["name"])]),
                _: 1
              }, 8, ["id"]), _createVNode(_component_ion_popover, {
                trigger: `volume-popover-button-${_ctx.isFullscreen}`,
                "show-backdrop": false
              }, {
                default: _withCtx(() => [_createVNode(_component_ion_content, null, {
                  default: _withCtx(() => [_createVNode(_component_ion_range, {
                    modelValue: _unref(volume),
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => _isRef(volume) ? volume.value = $event : null),
                    max: 1,
                    min: 0,
                    step: 0.1,
                    class: "video-player-contrast"
                  }, {
                    default: _withCtx(() => [_createVNode(_component_ion_icon, {
                      class: "video-player-contrast",
                      name: "volume-off",
                      slot: "start"
                    }), _createVNode(_component_ion_icon, {
                      class: "video-player-contrast",
                      name: "volume-high",
                      slot: "end"
                    })]),
                    _: 1
                  }, 8, ["modelValue"])]),
                  _: 1
                })]),
                _: 1
              }, 8, ["trigger"])]),
              _: 1
            }, 8, ["id", "collapse"])]),
            _: 1
          }), _createVNode(_component_ion_col, {
            size: "auto"
          }, {
            default: _withCtx(() => [_createVNode(_component_ion_button, {
              class: "video-player-contrast",
              onClick: _cache[4] || (_cache[4] = $event => emit('toggleFullscreen', _unref(currentTime)))
            }, {
              default: _withCtx(() => [_withDirectives(_createVNode(_component_ion_icon, {
                name: "expand"
              }, null, 512), [[_vShow, !_ctx.isFullscreen]]), _withDirectives(_createVNode(_component_ion_icon, {
                name: "contract"
              }, null, 512), [[_vShow, _ctx.isFullscreen]])]),
              _: 1
            })]),
            _: 1
          })]),
          _: 1
        })]),
        _: 1
      });
    };
  }
});